import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, ProgressBar, Alert, Spinner, Form, Button } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import AdminNavbar from '../components/AdminNavbar';
import '../styles/brand.css';

const SystemAnalytics = () => {
  const { currentUser, userProfile } = useAuth();
  const [analytics, setAnalytics] = useState({
    systemHealth: {
      uptime: '99.9%',
      responseTime: '245ms',
      errorRate: '0.1%',
      activeConnections: 247
    },
    userEngagement: {
      dailyActiveUsers: 0,
      weeklyActiveUsers: 0,
      monthlyActiveUsers: 0,
      avgSessionDuration: 0
    },
    contentMetrics: {
      totalModules: 0,
      totalLessons: 0,
      avgCompletionRate: 0,
      popularContent: []
    },
    performanceMetrics: {
      pageLoadTimes: [],
      apiResponseTimes: [],
      errorLogs: []
    }
  });
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('7d');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (currentUser) {
      loadAnalyticsData();
    }
  }, [currentUser, dateRange]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      
      // Load user engagement data
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      const users = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Load modules data
      const modulesCollection = collection(db, 'modules');
      const modulesSnapshot = await getDocs(modulesCollection);
      const modules = modulesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Load enrollment data for completion rates
      const enrollmentsCollection = collection(db, 'enrollments');
      const enrollmentsSnapshot = await getDocs(enrollmentsCollection);
      const enrollments = enrollmentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Calculate engagement metrics
      const now = new Date();
      const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      
      const dailyActiveUsers = users.filter(user => {
        const lastLogin = user.lastLoginAt?.toDate ? user.lastLoginAt.toDate() : new Date(user.lastLoginAt || 0);
        return lastLogin >= oneDayAgo;
      }).length;
      
      const weeklyActiveUsers = users.filter(user => {
        const lastLogin = user.lastLoginAt?.toDate ? user.lastLoginAt.toDate() : new Date(user.lastLoginAt || 0);
        return lastLogin >= oneWeekAgo;
      }).length;
      
      const monthlyActiveUsers = users.filter(user => {
        const lastLogin = user.lastLoginAt?.toDate ? user.lastLoginAt.toDate() : new Date(user.lastLoginAt || 0);
        return lastLogin >= oneMonthAgo;
      }).length;
      
      // Calculate completion rates
      const completedEnrollments = enrollments.filter(e => e.completed === true);
      const avgCompletionRate = enrollments.length > 0 ? (completedEnrollments.length / enrollments.length) * 100 : 0;
      
      // Mock performance data (in real app, this would come from monitoring service)
      const mockPageLoadTimes = [
        { page: 'Dashboard', avgTime: 245, samples: 1250 },
        { page: 'User Management', avgTime: 312, samples: 456 },
        { page: 'Reports', avgTime: 189, samples: 789 },
        { page: 'Analytics', avgTime: 567, samples: 234 }
      ];
      
      const mockApiResponseTimes = [
        { endpoint: '/api/users', avgTime: 89, samples: 5432 },
        { endpoint: '/api/modules', avgTime: 156, samples: 3211 },
        { endpoint: '/api/enrollments', avgTime: 234, samples: 2345 },
        { endpoint: '/api/analytics', avgTime: 445, samples: 1567 }
      ];
      
      const mockErrorLogs = [
        { timestamp: new Date(), level: 'warning', message: 'High memory usage detected', count: 3 },
        { timestamp: new Date(Date.now() - 3600000), level: 'error', message: 'Database connection timeout', count: 1 },
        { timestamp: new Date(Date.now() - 7200000), level: 'info', message: 'Backup completed successfully', count: 1 }
      ];
      
      setAnalytics({
        systemHealth: {
          uptime: '99.9%',
          responseTime: '245ms',
          errorRate: '0.1%',
          activeConnections: 247
        },
        userEngagement: {
          dailyActiveUsers,
          weeklyActiveUsers,
          monthlyActiveUsers,
          avgSessionDuration: 28 // minutes - mock data
        },
        contentMetrics: {
          totalModules: modules.length,
          totalLessons: modules.reduce((total, module) => total + (module.lessons?.length || 0), 0),
          avgCompletionRate: Math.round(avgCompletionRate),
          popularContent: modules.slice(0, 5).map(module => ({
            title: module.title,
            enrollments: enrollments.filter(e => e.moduleId === module.id).length,
            completions: enrollments.filter(e => e.moduleId === module.id && e.completed).length
          }))
        },
        performanceMetrics: {
          pageLoadTimes: mockPageLoadTimes,
          apiResponseTimes: mockApiResponseTimes,
          errorLogs: mockErrorLogs
        }
      });
      
    } catch (error) {
      devError('Error loading analytics data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAnalyticsData();
  };

  if (userProfile?.role !== 'admin') {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Access Denied</h4>
          <p>You do not have permission to access system analytics.</p>
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <>
        <AdminNavbar currentPage="analytics" />
        <Container className="mt-4">
          <div className="text-center my-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading system analytics...</p>
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar currentPage="analytics" />
      
      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="mb-3">System Analytics</h1>
              <p className="mission-text mb-4">
                Monitor system performance and usage analytics
              </p>
              <p className="mb-0">
                Track real-time system performance metrics, user engagement patterns, and content analytics to optimize platform performance and enhance user experience.
              </p>
            </Col>
            <Col lg={4} className="d-flex align-items-center justify-content-end">
              <div className="d-flex gap-2">
                <Form.Select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="me-2"
                  style={{ width: 'auto' }}
                >
                  <option value="1d">Last 24 Hours</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                  <option value="90d">Last 90 Days</option>
                </Form.Select>
                <Button
                  variant="outline-light"
                  onClick={handleRefresh}
                  disabled={refreshing}
                >
                  {refreshing ? <Spinner animation="border" size="sm" /> : 'Refresh'}
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="mt-4">

        {/* System Health Overview */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center h-100">
              <Card.Body>
                <h3 className="text-success">{analytics.systemHealth.uptime}</h3>
                <small className="text-muted">System Uptime</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center h-100">
              <Card.Body>
                <h3 className="text-primary">{analytics.systemHealth.responseTime}</h3>
                <small className="text-muted">Avg Response Time</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center h-100">
              <Card.Body>
                <h3 className="text-warning">{analytics.systemHealth.errorRate}</h3>
                <small className="text-muted">Error Rate</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center h-100">
              <Card.Body>
                <h3 className="text-info">{analytics.systemHealth.activeConnections}</h3>
                <small className="text-muted">Active Connections</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* User Engagement Metrics */}
        <Row className="mb-4">
          <Col md={6}>
            <Card>
              <Card.Header>
                <h5>User Engagement</h5>
              </Card.Header>
              <Card.Body>
                <Row className="text-center">
                  <Col md={4}>
                    <div className="mb-3">
                      <h4 className="text-primary">{analytics.userEngagement.dailyActiveUsers}</h4>
                      <small className="text-muted">Daily Active Users</small>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-3">
                      <h4 className="text-success">{analytics.userEngagement.weeklyActiveUsers}</h4>
                      <small className="text-muted">Weekly Active Users</small>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-3">
                      <h4 className="text-info">{analytics.userEngagement.monthlyActiveUsers}</h4>
                      <small className="text-muted">Monthly Active Users</small>
                    </div>
                  </Col>
                </Row>
                <div className="mt-3">
                  <div className="d-flex justify-content-between mb-1">
                    <small>Average Session Duration</small>
                    <small>{analytics.userEngagement.avgSessionDuration} minutes</small>
                  </div>
                  <ProgressBar 
                    now={(analytics.userEngagement.avgSessionDuration / 60) * 100} 
                    variant="info"
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card>
              <Card.Header>
                <h5>Content Metrics</h5>
              </Card.Header>
              <Card.Body>
                <Row className="text-center mb-3">
                  <Col md={6}>
                    <div>
                      <h4 className="text-primary">{analytics.contentMetrics.totalModules}</h4>
                      <small className="text-muted">Total Modules</small>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div>
                      <h4 className="text-success">{analytics.contentMetrics.avgCompletionRate}%</h4>
                      <small className="text-muted">Avg Completion Rate</small>
                    </div>
                  </Col>
                </Row>
                <div>
                  <div className="d-flex justify-content-between mb-1">
                    <small>Content Engagement</small>
                    <small>{analytics.contentMetrics.avgCompletionRate}%</small>
                  </div>
                  <ProgressBar 
                    now={analytics.contentMetrics.avgCompletionRate} 
                    variant="success"
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Performance Metrics */}
        <Row className="mb-4">
          <Col md={6}>
            <Card>
              <Card.Header>
                <h5>Page Load Times</h5>
              </Card.Header>
              <Card.Body>
                <Table size="sm">
                  <thead>
                    <tr>
                      <th>Page</th>
                      <th>Avg Load Time</th>
                      <th>Samples</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.performanceMetrics.pageLoadTimes.map((page, index) => (
                      <tr key={index}>
                        <td>{page.page}</td>
                        <td>{page.avgTime}ms</td>
                        <td>{page.samples}</td>
                        <td>
                          <Badge bg={page.avgTime < 300 ? 'success' : page.avgTime < 500 ? 'warning' : 'danger'}>
                            {page.avgTime < 300 ? 'Good' : page.avgTime < 500 ? 'Fair' : 'Slow'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card>
              <Card.Header>
                <h5>API Performance</h5>
              </Card.Header>
              <Card.Body>
                <Table size="sm">
                  <thead>
                    <tr>
                      <th>Endpoint</th>
                      <th>Avg Response</th>
                      <th>Requests</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.performanceMetrics.apiResponseTimes.map((api, index) => (
                      <tr key={index}>
                        <td><code>{api.endpoint}</code></td>
                        <td>{api.avgTime}ms</td>
                        <td>{api.samples}</td>
                        <td>
                          <Badge bg={api.avgTime < 200 ? 'success' : api.avgTime < 400 ? 'warning' : 'danger'}>
                            {api.avgTime < 200 ? 'Fast' : api.avgTime < 400 ? 'OK' : 'Slow'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Popular Content */}
        <Row className="mb-4">
          <Col md={6}>
            <Card>
              <Card.Header>
                <h5>Popular Content</h5>
              </Card.Header>
              <Card.Body>
                <Table size="sm">
                  <thead>
                    <tr>
                      <th>Module</th>
                      <th>Enrollments</th>
                      <th>Completions</th>
                      <th>Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.contentMetrics.popularContent.map((content, index) => (
                      <tr key={index}>
                        <td>{content.title}</td>
                        <td>{content.enrollments}</td>
                        <td>{content.completions}</td>
                        <td>
                          <Badge bg={content.enrollments > 0 ? 
                            ((content.completions / content.enrollments) * 100 > 70 ? 'success' : 
                             (content.completions / content.enrollments) * 100 > 50 ? 'warning' : 'danger') : 'secondary'}>
                            {content.enrollments > 0 ? Math.round((content.completions / content.enrollments) * 100) : 0}%
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card>
              <Card.Header>
                <h5>Recent System Events</h5>
              </Card.Header>
              <Card.Body>
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {analytics.performanceMetrics.errorLogs.map((log, index) => (
                    <div key={index} className="mb-3 pb-2 border-bottom">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <Badge bg={log.level === 'error' ? 'danger' : log.level === 'warning' ? 'warning' : 'info'}>
                            {log.level.toUpperCase()}
                          </Badge>
                          <span className="ms-2">{log.message}</span>
                        </div>
                        <small className="text-muted">
                          {log.timestamp.toLocaleString()}
                        </small>
                      </div>
                      {log.count > 1 && (
                        <small className="text-muted">Occurred {log.count} times</small>
                      )}
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default SystemAnalytics;